import React, { Component } from 'react';
import './PlannerMapDraftList.scss';
import { OrderListOrder, Region } from '../../../../types/orderTypes';
import { BundleJson, BundleStatus } from '../../../../types/bundleTypes';
import PlannerBundle from '../../planner/PlannerBundle';
import Modal from '../../../../components/modals/Modal';
import MovingOrdersToBundleModalContent, {
  onTriggerMoveOrdersToSelectedBundle,
} from '../../planner/MovingOrdersToBundleModalContent';
import Swal from 'sweetalert2';
import { Dayjs } from 'dayjs';

interface Props {
  bundles: BundleJson[];
  orders: OrderListOrder[];
  onSavedModification: () => void;
  onOrdersRemoved: (bundleId: string, orderIds: string[]) => void;
  onSelectBundleDraft?: (id: string) => void;
  onDeselectBundleDraft?: (id: string) => void;
  selectedBundlesDrafts: string[];
  region: Region;

  selectedDate?: Dayjs;
}

interface State {
  selectedBundleIds: string[];
  mapFocusedBundleId?: string;
  isOpenMovingBundleModal: boolean;
  selectedBundleToMoveOrders: string;
  bundleIdMoveFrom: string;
}

export default class PlannerMapDraftList extends Component<Props, State> {
  private readonly plannerBundleListRefs: {
    [bundleId: string]: React.RefObject<PlannerBundle>;
  };
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedBundleIds: [],
      mapFocusedBundleId: null,
      isOpenMovingBundleModal: false,
      selectedBundleToMoveOrders: '',
      bundleIdMoveFrom: '',
    };
    this.plannerBundleListRefs = {};
  }

  createBundlesRefs = () => {
    const { bundles } = this.props;
    bundles.forEach(
      bundle => (this.plannerBundleListRefs[bundle.id] = React.createRef())
    );
  };

  onSelectBundle = (bundle: BundleJson, expanded: boolean) => {
    const bundleDraftElement = document.getElementById(bundle.id);
    if (bundleDraftElement?.classList.contains('map-selection-animation')) {
      bundleDraftElement.classList.remove('map-selection-animation');
    }
    if (expanded) {
      this.setState({
        selectedBundleIds: [...this.state.selectedBundleIds, bundle.id],
      });
    } else {
      this.setState({
        selectedBundleIds: this.state.selectedBundleIds.filter(
          bundleId => bundleId !== bundle.id
        ),
      });
    }
  };

  openModalToSelectBundle = (bundleId: string) => {
    this.setState({
      isOpenMovingBundleModal: true,
      bundleIdMoveFrom: bundleId,
    });
  };

  onSelectBundleToMove = (bundleId: string) =>
    this.setState({ selectedBundleToMoveOrders: bundleId });

  closeModalOfMovingBundle = () =>
    this.setState({
      isOpenMovingBundleModal: false,
      selectedBundleToMoveOrders: '',
    });

  triggerMoveOrders = async () => {
    const { onSavedModification } = this.props;
    const { bundleIdMoveFrom, selectedBundleToMoveOrders } = this.state;
    const currentBundle = this.plannerBundleListRefs[bundleIdMoveFrom].current;
    const resp = await onTriggerMoveOrdersToSelectedBundle(
      bundleIdMoveFrom,
      selectedBundleToMoveOrders,
      currentBundle
    );
    if (typeof resp === 'boolean') {
      return;
    }
    if (resp) {
      currentBundle.clearSelectedOrdersAndStopMoving();
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: `Orders moved to the bundle ${selectedBundleToMoveOrders}`,
        showConfirmButton: false,
        timer: 2000,
      });
      await onSavedModification();
      this.setState({
        isOpenMovingBundleModal: false,
        bundleIdMoveFrom: '',
        selectedBundleToMoveOrders: '',
      });
    } else {
      await Swal.fire({
        position: 'center',
        icon: 'error',
        title: `Something went wrong. Selected orders ${selectedBundleToMoveOrders} didn't move to the bundle ${selectedBundleToMoveOrders}`,
        showConfirmButton: false,
        timer: 3000,
      });
      this.setState({
        isOpenMovingBundleModal: false,
        bundleIdMoveFrom: '',
        selectedBundleToMoveOrders: '',
      });
    }
  };

  render() {
    const { bundles, selectedBundlesDrafts, region } = this.props;
    const {
      mapFocusedBundleId,
      isOpenMovingBundleModal,
      selectedBundleToMoveOrders,
      bundleIdMoveFrom,
    } = this.state;
    if (bundles.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: 2,
            backgroundColor: '#f0f0f0',
          }}
        >
          No draft bundles
        </div>
      );
    }
    this.createBundlesRefs();
    return (
      <div>
        {isOpenMovingBundleModal ? (
          <Modal
            onClose={this.closeModalOfMovingBundle}
            buttons={[{ title: 'Select', onClick: this.triggerMoveOrders }]}
          >
            <MovingOrdersToBundleModalContent
              bundleList={bundles.filter(
                bundle => bundle.id !== bundleIdMoveFrom
              )}
              selectedBundle={selectedBundleToMoveOrders}
              onSelectBundle={this.onSelectBundleToMove}
            />
          </Modal>
        ) : null}
        {bundles.map(bundle => (
          <div
            key={bundle.id}
            className="planner-map-draft-list"
            id={bundle.id}
          >
            <PlannerBundle
              ref={this.plannerBundleListRefs[bundle.id]}
              bundle={bundle}
              expanded={this.state.selectedBundleIds.includes(bundle.id)}
              mapOnTop={bundle.id === mapFocusedBundleId}
              onExpand={this.onSelectBundle}
              onSavedModification={this.props.onSavedModification}
              onOrdersRemoved={this.props.onOrdersRemoved}
              onMapSelected={() =>
                this.setState({
                  mapFocusedBundleId: bundle.id,
                })
              }
              onSelectBundleDraft={
                bundle.status === BundleStatus.Draft
                  ? this.props.onSelectBundleDraft
                  : undefined
              }
              onDeselectBundleDraft={this.props.onDeselectBundleDraft}
              bundleDraftIsSelected={selectedBundlesDrafts.some(
                selectedBundlesDraft => selectedBundlesDraft === bundle.id
              )}
              region={region}
              openModalToSelectBundle={this.openModalToSelectBundle}
              selectedDate={this.props.selectedDate}
              itemColumns={[
                'InstructionType',
                'OrderNumber',
                'Address',
                'WindowTime',
                'EstimateTimeArrival',
              ]}
            />
          </div>
        ))}
      </div>
    );
  }
}
